/**
 * Route-entry prefetch cache (perf F7, docs/perf/2026-06-06-site-sweep.md).
 *
 * Pages are lazy chunks, so fetches living in their effects start only
 * after the chunk downloads and executes (~1.4s on 4G). Route-entry
 * components start the critical fetch during their own (eager) render
 * — in parallel with the chunk — and stash the in-flight promise here.
 * The page hook consumes it once on mount and falls back to a normal
 * fetch when absent, so the hook stays the single source of truth for
 * the resource.
 */

type Entry = { promise: Promise<unknown>; at: number }

const inflight = new Map<string, Entry>()

/** Entries older than this are leftovers of abandoned navigations —
 * consumers refetch fresh instead of reusing them. */
const MAX_AGE_MS = 30_000

/**
 * Starts `start()` once per key while a fresh entry exists — calling
 * from a render phase is safe (idempotent under StrictMode's double
 * render; that is the point: effects would only run after the lazy
 * chunk arrives, which is exactly the delay being removed).
 */
export function startRoutePrefetch(key: string, start: () => Promise<unknown>): void {
  const existing = inflight.get(key)
  if (existing && Date.now() - existing.at <= MAX_AGE_MS) return
  const promise = start()
  inflight.set(key, { promise, at: Date.now() })
  // An abandoned navigation leaves the promise unconsumed: silence the
  // unhandled rejection and evict, so a later visit refetches fresh
  // instead of consuming a cached failure.
  promise.catch(() => {
    const current = inflight.get(key)
    if (current?.promise === promise) inflight.delete(key)
  })
}

/** Consume-once: hands the prefetched promise to the page hook (or
 * null — stale or never started — meaning "fetch as usual"). */
export function consumeRoutePrefetch<T>(key: string): Promise<T> | null {
  const entry = inflight.get(key)
  if (!entry) return null
  inflight.delete(key)
  if (Date.now() - entry.at > MAX_AGE_MS) return null
  return entry.promise as Promise<T>
}
